import { useEffect, useRef, useState } from "react";
import { useAudioPlayer } from "@/context/AudioPlayerContext";
import { ytId } from "@/components/AudioMedia";
import { Play, Pause, X, AudioLines, Repeat, Youtube, Radio, SkipForward } from "lucide-react";

const fmt = (s) => (isFinite(s) ? `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}` : "0:00");

// Persistent, dismissible mini-player anchored at the app root so audio keeps
// playing while the user navigates. MP3/uploaded files use a real <audio>
// element (seekable + Media Session lock-screen controls); YouTube tracks use
// a compact iframe controlled via the postMessage JS API.
export default function GlobalAudioPlayer() {
  const { track, playing, setPlaying, toggle, close, hasQueue, next, channel } = useAudioPlayer();
  const audioRef = useRef(null);
  const ytRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [time, setTime] = useState("0:00");
  const [duration, setDuration] = useState("0:00");

  const isYouTube = track?.kind === "youtube";
  const yid = isYouTube ? ytId(track.url) : "";

  // Drive the <audio> element from the `playing` flag (MP3/file tracks).
  useEffect(() => {
    const a = audioRef.current;
    if (!a || isYouTube) return;
    if (playing) a.play().catch(() => setPlaying(false));
    else a.pause();
  }, [playing, track, isYouTube, setPlaying]);

  // Control the YouTube iframe via the postMessage command API.
  const ytCmd = (func) => {
    const w = ytRef.current?.contentWindow;
    if (w) w.postMessage(JSON.stringify({ event: "command", func, args: [] }), "*");
  };
  useEffect(() => {
    if (!isYouTube) return;
    const t = setTimeout(() => ytCmd(playing ? "playVideo" : "pauseVideo"), 350);
    return () => clearTimeout(t);
  }, [playing, track, isYouTube]);

  // Media Session API — lock-screen / headset controls (MP3 tracks).
  useEffect(() => {
    if (!track || isYouTube || !("mediaSession" in navigator)) return;
    try {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: track.title || "Dharmartha Audio",
        artist: track.category || "Dharmartha",
        album: "Dharmartha · Sangraha",
      });
      navigator.mediaSession.setActionHandler("play", () => setPlaying(true));
      navigator.mediaSession.setActionHandler("pause", () => setPlaying(false));
      navigator.mediaSession.setActionHandler("seekbackward", () => { const a = audioRef.current; if (a) a.currentTime = Math.max(0, a.currentTime - 10); });
      navigator.mediaSession.setActionHandler("seekforward", () => { const a = audioRef.current; if (a) a.currentTime = Math.min(a.duration || 0, a.currentTime + 10); });
    } catch { /* unsupported */ }
  }, [track, isYouTube, setPlaying]);

  useEffect(() => { setProgress(0); setTime("0:00"); setDuration("0:00"); }, [track?.id]);

  if (!track) return null;

  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    a.currentTime = ((e.clientX - rect.left) / rect.width) * a.duration;
  };

  return (
    <div data-testid="global-audio-player" className="fixed inset-x-0 bottom-0 z-[80] border-t border-[var(--gold)]/40 bg-[#1c120b]/95 text-[#f7f3ec] backdrop-blur-md shadow-[0_-8px_30px_rgba(0,0,0,0.35)]">
      {!isYouTube && (
        <audio
          ref={audioRef}
          src={track.url}
          loop={!!track.loop}
          autoPlay
          preload="metadata"
          onLoadedMetadata={(e) => setDuration(fmt(e.currentTarget.duration))}
          onTimeUpdate={(e) => {
            const a = e.currentTarget;
            setProgress(a.duration ? (a.currentTime / a.duration) * 100 : 0);
            setTime(fmt(a.currentTime));
          }}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => (hasQueue ? next() : setPlaying(false))}
        />
      )}

      <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-2.5 md:gap-4 md:px-6">
        {/* Thumbnail */}
        {isYouTube && yid ? (
          <iframe
            ref={ytRef}
            title={track.title}
            data-testid="global-audio-yt"
            src={`https://www.youtube.com/embed/${yid}?enablejsapi=1&autoplay=1&rel=0&modestbranding=1${track.loop ? `&loop=1&playlist=${yid}` : ""}`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            className="h-12 w-20 shrink-0 rounded border border-white/10 md:h-14 md:w-24"
          />
        ) : (
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded bg-[var(--saffron)]/90">
            <AudioLines className="h-5 w-5 text-white" />
          </div>
        )}

        {/* Play / pause (toggles both MP3 and YouTube) */}
        <button
          data-testid="global-audio-toggle"
          onClick={toggle}
          aria-label={playing ? "Pause" : "Play"}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[var(--gold)] text-[var(--gold)] transition-colors hover:bg-[var(--gold)] hover:text-[var(--ink)]"
        >
          {playing ? <Pause className="h-4 w-4" fill="currentColor" /> : <Play className="h-4 w-4" fill="currentColor" />}
        </button>

        {hasQueue && (
          <button data-testid="global-audio-next" onClick={next} aria-label="Next" className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-full text-white/70 transition-colors hover:text-[var(--gold)] sm:flex">
            <SkipForward className="h-4 w-4" />
          </button>
        )}

        {/* Title + progress */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {channel ? (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-sm bg-red-600 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white"><Radio className="h-2.5 w-2.5" /> {channel.lang || "Live"}</span>
            ) : isYouTube ? <Youtube className="h-3.5 w-3.5 shrink-0 text-[var(--gold)]" /> : <AudioLines className="h-3.5 w-3.5 shrink-0 text-[var(--gold)]" />}
            <p className="truncate text-sm font-medium">{channel ? `${channel.title} · ` : ""}{track.title || "Audio"}</p>
            {track.loop && <Repeat className="h-3 w-3 shrink-0 text-[var(--gold)]" />}
            {track.category && <span className="hidden shrink-0 rounded-full bg-white/10 px-2 py-0.5 text-[10px] text-white/70 sm:inline">{track.category}</span>}
          </div>
          {isYouTube ? (
            <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-white/10">
              <div className={`h-full w-1/3 rounded-full bg-[var(--gold)]/70 ${playing ? "animate-pulse" : ""}`} />
            </div>
          ) : (
            <div className="mt-1.5 flex items-center gap-2">
              <span className="w-8 text-right text-[10px] tabular-nums text-white/55">{time}</span>
              <div onClick={seek} className="h-1.5 flex-1 cursor-pointer overflow-hidden rounded-full bg-white/15">
                <div className="h-full rounded-full bg-[var(--gold)] transition-[width] duration-200" style={{ width: `${progress}%` }} />
              </div>
              <span className="w-8 text-[10px] tabular-nums text-white/55">{duration}</span>
            </div>
          )}
        </div>

        {/* Dismiss */}
        <button
          data-testid="global-audio-close"
          onClick={close}
          aria-label="Close player"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white/60 transition-colors hover:bg-white/10 hover:text-white"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
